import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_KEY = 'taskmaster_session_token';

const api = axios.create({
  baseURL: API,
});

// Attach Bearer token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(SESSION_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authAPI = {
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  admins: () => api.get('/users/admins'),
  assignable: (groupId) => api.get('/users/assignable', { params: groupId ? { group_id: groupId } : {} }),
  onboardingStatus: () => api.get('/users/onboarding-status'),
  toggleBan: (userId) => api.put(`/users/${userId}/ban`),
  changeRole: (userId, role) => api.put(`/users/${userId}/role`, { role }),
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
};

// Teams (formerly Groups)
export const teamsAPI = {
  list: () => api.get('/teams'),
  get: (teamId) => api.get(`/teams/${teamId}`),
  create: (data) => api.post('/teams', data),
  delete: (teamId) => api.delete(`/teams/${teamId}`),
  invite: (teamId, email) => api.post(`/teams/${teamId}/invite`, { email }),
  acceptInvite: (token) => api.post('/teams/accept-invite', { token }),
  cancelInvite: (teamId, email) => api.delete(`/teams/${teamId}/invite/${encodeURIComponent(email)}`),
  removeMember: (teamId, userId) => api.delete(`/teams/${teamId}/members/${userId}`),
  leave: (teamId) => api.post(`/teams/${teamId}/leave`),
};

// Keep backward compat alias
export const groupsAPI = teamsAPI;

// Labels
export const labelsAPI = {
  list: () => api.get('/labels'),
  create: (data) => api.post('/labels', data),
  update: (labelId, data) => api.put(`/labels/${labelId}`, data),
  delete: (labelId) => api.delete(`/labels/${labelId}`),
};

// Tasks
export const tasksAPI = {
  list: (params) => api.get('/tasks', { params }),
  get: (taskId) => api.get(`/tasks/${taskId}`),
  create: (data) => api.post('/tasks', data),
  update: (taskId, data) => api.put(`/tasks/${taskId}`, data),
  delete: (taskId) => api.delete(`/tasks/${taskId}`),
  archive: (taskId) => api.post(`/tasks/${taskId}/archive`),
  unarchive: (taskId) => api.post(`/tasks/${taskId}/unarchive`),
  
  // Parts
  addPart: (taskId, data) => api.post(`/tasks/${taskId}/parts`, data),
  updatePart: (taskId, partId, data) => api.put(`/tasks/${taskId}/parts/${partId}`, data),
  deletePart: (taskId, partId) => api.delete(`/tasks/${taskId}/parts/${partId}`),
  
  // Tools
  addTool: (taskId, data) => api.post(`/tasks/${taskId}/tools`, data),
  deleteTool: (taskId, toolId) => api.delete(`/tasks/${taskId}/tools/${toolId}`),
  
  // Attachments
  uploadAttachment: (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAttachment: (taskId, attachmentId) => api.delete(`/tasks/${taskId}/attachments/${attachmentId}`),
  getAttachmentUrl: (taskId, attachmentId) => {
    const token = localStorage.getItem('taskmaster_session_token');
    return `${API}/tasks/${taskId}/attachments/${attachmentId}${token ? `?token=${token}` : ''}`;
  },
  
  // Sign-off
  addSignOff: (taskId, data) => api.post(`/tasks/${taskId}/sign-off`, data),
};

// Orders
export const ordersAPI = {
  list: () => api.get('/orders'),
  get: (orderId) => api.get(`/orders/${orderId}`),
  create: (taskIds) => api.post('/orders', { task_ids: taskIds }),
  update: (orderId, data) => api.put(`/orders/${orderId}`, data),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// AI
export const aiAPI = {
  suggest: (data) => api.post('/ai/suggest', data),
  generateQuote: (data) => api.post('/ai/generate-quote', data),
};

// Time Tracking
export const timeAPI = {
  getActive: () => api.get('/time/active'),
  checkIn: (taskId) => api.post(`/time/checkin/${taskId}`),
  checkOut: (note = '') => api.post('/time/checkout', null, { params: { note } }),
  addManual: (taskId, data) => api.post(`/time/manual/${taskId}`, data),
  deleteEntry: (entryId) => api.delete(`/time/entries/${entryId}`),
  getTaskEntries: (taskId) => api.get(`/time/entries/${taskId}`),
  getReport: (params) => api.get('/time/report', { params }),
};

// Quotes
export const quotesAPI = {
  list: (params) => api.get('/quotes', { params }),
  get: (quoteId) => api.get(`/quotes/${quoteId}`),
  create: (data) => api.post('/quotes', data),
  update: (quoteId, data) => api.put(`/quotes/${quoteId}`, data),
  updateStatus: (quoteId, status) => api.patch(`/quotes/${quoteId}/status`, { status }),
  delete: (quoteId) => api.delete(`/quotes/${quoteId}`),
  downloadPdf: (quoteId) => api.get(`/quotes/${quoteId}/pdf`, { responseType: 'blob' }),
};

// Export
export const exportAPI = {
  tasks: (format = 'csv') => `${API}/export/tasks?format=${format}`,
};

// Admin
export const adminAPI = {
  stats: () => api.get('/admin/stats'),
  tasks: (params) => api.get('/admin/tasks', { params }),
  teams: () => api.get('/admin/teams'),
};

export default api;
